import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, Teleport as _Teleport, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }

import { ref, computed, nextTick, onMounted, onUnmounted, watch, createApp } from "vue";
import mapboxgl, { LngLatLike } from "mapbox-gl";
import { storeToRefs } from "pinia";
import "mapbox-gl/dist/mapbox-gl.css";

import { useLocationsStore } from "~/js/store/locations";
import { Translations } from "~/js/interfaces/translations";
import { Location } from "~/js/interfaces/locations";
import { getTranslation } from "~/js/composables/translations";
import { getZoomForRadiusKm } from "~/js/composables/mapbox-geocoding";

import MarkerButton from "~/vue/atoms/markers/MarkerButton.vue";
import CardLocationSidebar from "~/vue/molecules/cards/CardLocationSidebar.vue";
import ButtonPrimary from "~/vue/atoms/buttons/ButtonPrimary.vue";

interface Props {
	translations?: Translations | null;
	utilities?: string;
	hasToggle?: boolean;
	sidebarOpen?: boolean;
	isDesktop?: boolean;
	desktopSidebarRatio?: number;
}
const DESKTOP_SIDEBAR_RATIO = 2 / 3;
const MOBILE_TOP_OVERLAY_PADDING = 288;


export default /*@__PURE__*/_defineComponent({
  __name: 'MapLocations',
  props: {
    translations: {},
    utilities: { default: "" },
    hasToggle: { type: Boolean, default: true },
    sidebarOpen: { type: Boolean, default: false },
    isDesktop: { type: Boolean, default: false },
    desktopSidebarRatio: { default: DESKTOP_SIDEBAR_RATIO }
  },
  emits: ["bounds-change"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const locationsStore = useLocationsStore();
const { locations, searchPlace, radiusKm } = storeToRefs(locationsStore);

const mapContainer = ref(null);
let map: mapboxgl.Map | null = null;
const activeLocation = ref<Location | null>(null);
const unclusteredFeatures = ref<any[]>([]);
const activeMarkers = ref<mapboxgl.Marker[]>([]);
const setActiveLocation = (location: Location | null) => {
	activeLocation.value = location;
	emit("popup-change", Boolean(location));
};

const getMapPadding = () => {
	if (!props.isDesktop) {
		return {
			left: 0,
			right: 0,
			top: MOBILE_TOP_OVERLAY_PADDING,
			bottom: 0,
		};
	}

	if (!props.sidebarOpen || typeof window === "undefined") {
		return 0;
	}

	return {
		left: Math.round(window.innerWidth * props.desktopSidebarRatio),
		right: 0,
		top: 0,
		bottom: 0,
	};
};

const geoJSONObject = computed(() => {
	if (!locations.value) return { type: "FeatureCollection", features: [] };
	return {
		type: "FeatureCollection",
		features: locations.value.map((location) => ({
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: [Number(location.document.longitude), Number(location.document.latitude)],
			},
			properties: location.document ?? {},
		})),
	};
});

const mapboxApi = computed(() => import.meta.env.VITE_MAPBOX_API ?? "");

const openPopup = (propertyId: string) => {
	const location = locations.value.find((loc) => loc.document.id === propertyId);
	if (location) {
		setActiveLocation(location.document);
	}
};

const focusMarker = async (coordinates: LngLatLike) => {
	await nextTick();
	if (!map) return;

	map.easeTo({
		center: coordinates,
		zoom: Math.max(map.getZoom(), 14),
		padding: getMapPadding(),
	});
};

const updateUnclusteredFeatures = () => {
	if (!map || !map.getSource("locations")) return;
	const features = map.querySourceFeatures("locations", {
		filter: ["!", ["has", "point_count"]],
	});
	unclusteredFeatures.value = features;
};

// Emit bounds for filtering
const emitBounds = () => {
	if (!map) return;
	const bounds = map.getBounds();
	emit("bounds-change", {
		_ne: bounds.getNorthEast(),
		_sw: bounds.getSouthWest(),
	});
};

watch(unclusteredFeatures, (newFeatures) => {
	activeMarkers.value.forEach((marker) => marker.remove());
	activeMarkers.value = [];

	for (const feature of newFeatures) {
		const el = document.createElement("div");

		const markerApp = createApp(MarkerButton, {
			type: feature.properties.type,
			status: feature.properties.availableNow ? "available" : "soon",
			title: feature.properties.title,
		});
		markerApp.mount(el);

		const newMarker = new mapboxgl.Marker(el).setLngLat(feature.geometry.coordinates as LngLatLike).addTo(map!);

		el.addEventListener("click", (event) => {
			event.stopPropagation();
			openPopup(feature.properties.id);
			void focusMarker(feature.geometry.coordinates as LngLatLike);
		});

		activeMarkers.value.push(newMarker);
	}
});

watch(geoJSONObject, (newData) => {
	if (map) {
		const source = map.getSource("locations") as mapboxgl.GeoJSONSource;
		if (source) {
			source.setData(newData);
		}
	}
});

const flyToSearchPlace = () => {
	if (!map || !searchPlace.value) {
		return;
	}

	map.flyTo({
		center: [searchPlace.value.longitude, searchPlace.value.latitude],
		zoom: getZoomForRadiusKm(radiusKm.value ?? 50),
		padding: getMapPadding(),
	});
};

watch(searchPlace, () => {
	flyToSearchPlace();
});

watch(radiusKm, () => {
	flyToSearchPlace();
});

onMounted(() => {
	if (!mapContainer.value) return;

	mapboxgl.accessToken = mapboxApi.value;

	const mobileBounds = [
		[2.308490704275215, 48.47604854348788], // SW
		[6.877044457833449, 53.57970665931481], // NE
	];

	const desktopBounds = [
		[-0.1437664080393688, 50.301969365999355], // SW
		[7.8884511669152175, 52.4699634262669], // NE
	];

	const initialBounds = props.isDesktop ? desktopBounds : mobileBounds;
	const fitBoundsPadding = getMapPadding();

	map = new mapboxgl.Map({
		container: mapContainer.value,
		style: "mapbox://styles/mapbox/light-v11",
		bounds: initialBounds as mapboxgl.LngLatBoundsLike,
		fitBoundsOptions: { padding: fitBoundsPadding },
		projection: { name: "mercator" },
		dragRotate: false,
		pitchWithRotate: false,
	});

	map.touchZoomRotate.disableRotation();

	const nav = new mapboxgl.NavigationControl({ showCompass: false });
	// Use top-right for mobile to avoid cookie banner, bottom-left for desktop
	const position = props.isDesktop ? "bottom-left" : "top-right";
	map.addControl(nav, position);

	map.on("load", () => {
		emitBounds();
		map.on("moveend", emitBounds);
		flyToSearchPlace();

		map?.addSource("locations", {
			type: "geojson",
			data: geoJSONObject.value,
			cluster: true,
			clusterMaxZoom: 14,
			clusterRadius: 50,
		});

		map?.addLayer({
			id: "clusters",
			type: "circle",
			source: "locations",
			filter: ["has", "point_count"],
			paint: {
				"circle-color": "#1a1a1a",
				"circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
			},
		});

		map?.addLayer({
			id: "cluster-count",
			type: "symbol",
			source: "locations",
			filter: ["has", "point_count"],
			layout: {
				"text-field": "{point_count_abbreviated}",
				"text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
				"text-size": 14,
			},
			paint: { "text-color": "#ffffff" },
		});

		const onMapMove = () => setTimeout(updateUnclusteredFeatures, 0);
		map?.on("data", (e) => {
			if (e.dataType === "source" && e.sourceId === "locations") {
				onMapMove();
			}
		});
		map?.on("moveend", onMapMove);

		updateUnclusteredFeatures();

		map?.on("click", "clusters", (e) => {
			setActiveLocation(null);

			const cluster = e.features?.[0];
			const source = map?.getSource("locations") as mapboxgl.GeoJSONSource | undefined;
			const clusterId = Number(cluster?.properties?.cluster_id);
			const coordinates = (cluster?.geometry as GeoJSON.Point | undefined)?.coordinates;

			if (!source || !Number.isFinite(clusterId) || !coordinates) return;

			source.getClusterExpansionZoom(clusterId, (err, zoom) => {
				if (err || !map || !Number.isFinite(zoom)) return;

				map.stop();
				map.easeTo({
					center: coordinates as LngLatLike,
					// Mapbox can return the current zoom for a freshly re-clustered source.
					// Always advance at least one level so the clicked cluster actually expands.
					zoom: Math.max(zoom, map.getZoom() + 1),
					padding: getMapPadding(),
				});
			});
		});

		map?.on("click", () => {
			setActiveLocation(null);
		});
	});

});

onUnmounted(() => {
	activeMarkers.value.forEach((marker) => marker.remove());
	if (map) {
		map.remove();
		map = null;
	}

});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["map-locations rounded-bl-80 rounded-br-80 relative flex h-full w-full grow flex-col overflow-hidden", [__props.utilities, __props.sidebarOpen ? 'sidebar-is-open' : '', activeLocation.value ? 'popup-is-open' : '']])
  }, [
    _createElementVNode("div", {
      ref_key: "mapContainer",
      ref: mapContainer,
      class: "h-full w-full grow"
    }, null, 512),
    (__props.hasToggle)
      ? (_openBlock(), _createBlock(ButtonPrimary, {
          key: 0,
          size: "small",
          icon: "list",
          type: "button",
          "icon-position": "after",
          content: _unref(getTranslation)(__props.translations, 'View list'),
          utilities: "fixed left-1/2 -translate-x-1/2 bottom-13 z-[500]",
          ga: {
				category: 'location-view-toggle',
				action: 'click',
				label: 'View list',
			},
          onClick: _cache[0] || (_cache[0] = () => _unref(locationsStore).toggleView('list'))
        }, null, 8, ["content"]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (activeLocation.value && __props.isDesktop)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(CardLocationSidebar, {
              location: activeLocation.value,
              translations: __props.translations,
              utilities: "absolute right-6 top-32 z-[900] w-[24rem] max-w-[calc(100vw-3rem)] shadow-2xl xl:w-[26rem]",
              "has-close": true,
              stacked: true,
              onClose: _cache[1] || (_cache[1] = ($event: any) => (setActiveLocation(null)))
            }, null, 8, ["location", "translations"])
          ]))
        : _createCommentVNode("", true),
      (activeLocation.value && !__props.isDesktop)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(CardLocationSidebar, {
              location: activeLocation.value,
              translations: __props.translations,
              utilities: "fixed left-4 right-4 top-1/2 z-[1200] -translate-y-1/2 shadow-2xl",
              "has-close": true,
              stacked: true,
              onClose: _cache[2] || (_cache[2] = ($event: any) => (setActiveLocation(null)))
            }, null, 8, ["location", "translations"])
          ]))
        : _createCommentVNode("", true)
    ]))
  ], 2))
}
}

})